import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import StopIcon from '@material-ui/icons/Stop';
import styled from 'styled-components';

import type { SendResult, TokenNotice } from '../../hooks/useMaiaChat';
import type { AnswerFeedback, ChatMessage } from '../../types';
import defaults from '../../translations/en.json';
import { MessageList } from './MessageList';

type ActiveChatViewProps = {
  messages: ChatMessage[];
  /** Which records the answers draw on: the chat's locked range. */
  rangeNote: string;
  isSending: boolean;
  /** The pending reply passed SLOW_REPLY_MS: ask whether to cancel it. */
  isSlowReply?: boolean;
  /** The agent is still on an earlier question (423): ask whether to cancel it. */
  isAgentBusy?: boolean;
  isCancelling?: boolean;
  /** Yes: stop the question on the agent; the composer unlocks. */
  onCancel?: () => void;
  /** No: keep waiting; the prompt comes back later. */
  onKeepWaiting?: () => void;
  error?: string | null;
  onSend: (message: string) => Promise<SendResult> | SendResult;
  /** Rates an answer (null undoes it); false when it was not saved. */
  onRateAnswer?: (
    messageId: string,
    feedback: AnswerFeedback | null,
  ) => Promise<boolean>;
  smallWidget?: boolean;
  /** The % of the agency's token pool used, or the limit (red). Send stays on. */
  tokenNotice?: TokenNotice | null;
  onDismissTokenNotice?: () => void;
};

const Root = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background-color: ${(props) => props.theme.palette.background.paper};
  color: ${(props) => props.theme.palette.text.primary};
`;

/** Which records the answers draw on: the chat's 365 days. */
const RangeNote = styled.p`
  flex: none;
  margin: 0;
  padding: 6px 12px;
  font-size: 0.85rem;
  text-align: center;
  color: ${(props) =>
    props.theme.palette.text.readOnly || props.theme.palette.text.secondary};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const ErrorText = styled.p`
  margin: 0;
  padding: 4px 12px;
  color: ${(props) => props.theme.palette.error?.main || '#f44336'};
  font-size: 0.8rem;
`;

/** Sits in the transcript under the typing dots, where the user is looking. */
const CancelPrompt = styled.div`
  align-self: flex-start;
  max-width: 88%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  padding: 7px 10px;
  border-radius: 8px 8px 8px 2px;
  font-size: 1rem;
  line-height: 1.4;
  border: 1px solid ${(props) => props.theme.palette.divider};
  background-color: ${(props) =>
    props.theme.palette.background.dark1 ||
    props.theme.palette.background.default};
  color: ${(props) => props.theme.palette.text.primary};
  && > span {
    flex: 1 1 auto;
  }
  && button {
    font-size: 0.75rem;
    min-width: 0;
    padding: 2px 10px;
  }
`;

/** The token note: the % used, or the limit in red so it cannot be missed. */
const TokenNote = styled.div<{ $limitReached: boolean }>`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 12px 6px;
  padding: 4px 4px 4px 10px;
  border-radius: 4px;
  font-size: 0.85rem;
  line-height: 1.4;
  font-weight: ${(props) => (props.$limitReached ? 500 : 'normal')};
  border: 1px solid
    ${(props) =>
      props.$limitReached
        ? props.theme.palette.error.main
        : props.theme.palette.divider};
  background-color: ${(props) =>
    props.$limitReached
      ? props.theme.palette.error.main
      : props.theme.palette.background.dark1 ||
        props.theme.palette.background.default};
  color: ${(props) =>
    props.$limitReached
      ? props.theme.palette.error.contrastText
      : props.theme.palette.text.primary};
  && > span {
    flex: 1 1 auto;
  }
  && button {
    color: inherit;
  }
`;

const Composer = styled.div`
  flex: none;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid ${(props) => props.theme.palette.divider || '#555'};
`;

/**
 * Compact composer: smaller type and a darker field than MUI's default outlined
 * input. Element selectors on purpose: inside the dashboard the MUI class names
 * are generated per app, so `.MuiOutlinedInput-*` cannot be relied on.
 * Structure: FormControl > InputBase div > textarea (+ fieldset outline).
 */
const Draft = styled(TextField)`
  && > div {
    font-size: 1rem;
    padding: 0;
    background-color: ${(props) =>
      props.theme.palette.background.dark1 ||
      props.theme.palette.background.default};
  }
  && textarea {
    font-size: 1rem;
    /* px, not a ratio: one row is 20 + 2 * 8 = 36px whatever the root font size,
       the same height as SendButton */
    line-height: 20px;
    padding: 8px 10px;
  }
  && textarea::placeholder {
    font-size: 1rem;
  }
  && fieldset {
    border-color: ${(props) => props.theme.palette.divider};
  }
`;

const SendButton = styled(IconButton)`
  && {
    width: 44px;
    /* one row of Draft, so the two line up */
    height: 36px;
    border-radius: 4px;
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) =>
      props.theme.palette.type === 'dark'
        ? props.theme.palette.common.black
        : props.theme.palette.common.white};
    &:hover {
      background-color: ${(props) => props.theme.palette.primary.main};
      opacity: 0.9;
    }
    &.Mui-disabled {
      opacity: 0.45;
    }
  }
`;

/** Send's red twin while MAIA answers. */
const StopButton = styled(SendButton)`
  && {
    background-color: ${(props) => props.theme.palette.error.main};
    color: ${(props) => props.theme.palette.error.contrastText};
    &:hover {
      background-color: ${(props) => props.theme.palette.error.main};
    }
  }
`;

/** The live conversation: transcript plus composer. Range/detail live in the header. */
const ActiveChatView = ({
  messages,
  rangeNote,
  isSending,
  isSlowReply,
  isAgentBusy,
  isCancelling,
  onCancel,
  onKeepWaiting,
  error,
  onSend,
  onRateAnswer,
  tokenNotice,
  onDismissTokenNotice,
}: ActiveChatViewProps) => {
  const [draft, setDraft] = useState('');
  // The next question can be typed while MAIA answers; it is sent after the
  // reply or after Yes cancels it (one question per conversation at a time).
  // At the token limit Send stays on: the gateway decides each time.
  const canSend = !isSending && !isCancelling;

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !canSend) {
      return;
    }
    setDraft('');
    if ((await onSend(text)) === 'retry') {
      // not answered: give the question back, unless a new one was typed meanwhile
      setDraft((current) => current || text);
    }
  };

  let promptText: string | null = null;
  if (isSending && isSlowReply) {
    promptText = defaults.slowReply;
  } else if (isAgentBusy) {
    promptText = defaults.agentBusy;
  }

  const cancelPrompt = promptText ? (
    <CancelPrompt role="group" aria-label={promptText}>
      <span>{promptText}</span>
      <Button
        size="small"
        variant="contained"
        color="primary"
        disabled={isCancelling}
        onClick={onCancel}
      >
        {isCancelling ? defaults.cancelling : defaults.yes}
      </Button>
      <Button
        size="small"
        variant="outlined"
        disabled={isCancelling}
        onClick={onKeepWaiting}
      >
        {defaults.no}
      </Button>
    </CancelPrompt>
  ) : null;

  return (
    <Root data-testid="maia-active-chat">
      <RangeNote>{rangeNote}</RangeNote>
      <MessageList
        messages={messages}
        isTyping={isSending}
        footer={cancelPrompt}
        onRateAnswer={onRateAnswer}
      />
      {error ? <ErrorText role="alert">{error}</ErrorText> : null}
      {tokenNotice ? (
        <TokenNote
          role={tokenNotice.limitReached ? 'alert' : 'status'}
          $limitReached={tokenNotice.limitReached}
        >
          <span>{tokenNotice.text}</span>
          <IconButton
            size="small"
            aria-label={defaults.tokenNoticeClose}
            onClick={onDismissTokenNotice}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </TokenNote>
      ) : null}
      <Composer>
        <Draft
          fullWidth
          multiline
          maxRows={4}
          variant="outlined"
          size="small"
          placeholder={defaults.messagePlaceholder}
          inputProps={{
            'aria-label': defaults.messagePlaceholder,
          }}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.shiftKey) {
              return;
            }
            event.preventDefault();
            // a held key repeats, and Enter also confirms IME composition:
            // neither is a send
            if (event.repeat || event.nativeEvent.isComposing) {
              return;
            }
            handleSend();
          }}
        />
        {isSending ? (
          // while MAIA answers, Send becomes Stop: the same cancel as the slow-reply Yes
          <StopButton
            aria-label={defaults.stopAnswer}
            title={defaults.stopAnswer}
            disabled={isCancelling}
            onClick={onCancel}
          >
            <StopIcon fontSize="small" />
          </StopButton>
        ) : (
          <SendButton
            aria-label={defaults.send}
            disabled={!canSend || !draft.trim()}
            onClick={handleSend}
          >
            <ArrowForwardIcon fontSize="small" />
          </SendButton>
        )}
      </Composer>
    </Root>
  );
};

export default ActiveChatView;
